import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { omit, uniq } from 'lodash';
import React, { memo, useEffect, useMemo } from 'react';
import { Query, useQueryWithStore } from 'react-admin';
import { Grid } from '@material-ui/core/';

import UserAuthorizationDatagrid from './UserAuthorizationDatagrid';
import { USERTYPE } from '../EnumUserType';

const useStyles = makeStyles({
  label: {
    color: '#424242',
    fontSize: '14px',
    display: 'inline-block'
  },
  progress: {
    display: 'flex',
    justifyContent: 'center'
  }
});

/**
 * Display the circular progress component
 */
const Progress = memo(() => {
  const classes = useStyles();

  return (
    <div className={classes.progress}>
      <CircularProgress />
    </div>
  );
});

/**
 * Component to handle which component to be displayed
 * @param {*} props
 */
const UserAuthorizationUpdate = memo(props => {
  const {
    loaded,
    loading,
    userType,
    authorizations = [],
    newAuthorizations,
    onChangeNewAuthorizations
  } = props;
  // when perf debugging, check what's changed in the properties
  //useWhyDidYouUpdate('UserAuthorizationUpdate', props);

  const classes = useStyles();
  useEffect(() => {
    // When there are new unsaved authorizations on the local state,
    // check if they are really not saved on API and notify if we need to prevent navigation
    if (newAuthorizations && newAuthorizations.length) {
      const newAuthorizationsNotSavedExists = newAuthorizations.some(newAuth =>
        authorizations.every(auth => auth.fundId !== newAuth.fundId)
      );
      onChangeNewAuthorizations(newAuthorizationsNotSavedExists);
    }
  }, [authorizations, newAuthorizations, onChangeNewAuthorizations]);

  const totalAfterFilters = authorizations.length;

  if (totalAfterFilters > 0) {
    if (
      userType === USERTYPE.regularContact ||
      userType === USERTYPE.investorAdmin
    ) {
      return (
        <UserAuthorizationRegularContact
          {...props}
          authorizations={authorizations}
        />
      );
    } else if (userType === USERTYPE.fundAdmin) {
      return (
        <UserAuthorizationFundAdmin
          {...props}
          authorizations={authorizations}
        />
      );
    }
  } else if (loading) {
    return <Progress />;
  } else if (loaded && newAuthorizations.length > 0) {
    // when no authorizations found and adding a new ones
    return (
      <UserAuthorizationDatagrid
        {...omit(props, ['newAuthorizations'])}
        userType={userType}
        groupBy={
          userType === USERTYPE.regularContact ||
          userType === USERTYPE.investorAdmin
            ? 'investorId'
            : 'fundFamilyId'
        }
        groupByLabel={
          userType === USERTYPE.regularContact ||
          userType === USERTYPE.investorAdmin
            ? 'investorName'
            : 'fundFamilyName'
        }
        optionText="fundName"
        optionValue="fundId"
        authorizations={newAuthorizations}
      />
    );
  } else {
    return (
      <Grid item>
        <span className={classes.label}>
          There are no Access Rights to be displayed
        </span>
      </Grid>
    );
  }
});

/**
 * Component to get all data to display the datagrid for regular contact
 * @param {*} props
 */
export const UserAuthorizationRegularContact = props => {
  const {
    userType,
    authorizations,
    newAuthorizations,
    selectedAssignmentMode,
    creation,
    wizardMode
  } = props;
  const classes = useStyles();

  const uniqAuthorizations = useMemo(() => {
    return uniq(authorizations.map(item => item.fundId));
  }, [authorizations]);

  const { loading, data: fundInvestors } = useQueryWithStore({
    type: 'getManyReference',
    resource: 'fundinvestors',
    payload: {
      target: 'fundId',
      id: uniqAuthorizations,
      pagination: { perPage: -1 },
      options: { chunk: true }
    }
  });

  if (loading) {
    return <Progress />;
  } else if (fundInvestors.length > 0) {
    var fundMap = new Map(fundInvestors.map(i => [i.fundId, i.fundName]));
    var invMap = new Map(
      fundInvestors.map(i => [i.investorId, i.investorName])
    );

    // Creation of the data to be send to the datagrid
    // Adding the fund name and the investor name
    const dataWithInvestors = authorizations.map(auth => {
      const fundName = fundMap.get(auth.fundId);
      const investorName = invMap.get(auth.investorId);
      return {
        ...auth,
        fundName: fundName,
        investorName: investorName
      };
    });
    return (
      <UserAuthorizationDatagrid
        {...omit(props, ['newAuthorizations'])}
        userType={userType}
        groupBy="investorId"
        groupByLabel="investorName"
        optionText="fundName"
        optionValue="fundId"
        authorizations={
          selectedAssignmentMode === 'import' || !creation
            ? dataWithInvestors.concat(newAuthorizations)
            : newAuthorizations
        }
        creation={creation}
        wizardMode={wizardMode}
      />
    );
  }

  return (
    <span className={classes.label}>
      There are no Access Rights to be displayed
    </span>
  );
};

/**
 * Component to get all data to display the datagrid for fund admin contact
 * @param {*} props
 */
export const UserAuthorizationFundAdmin = props => {
  const {
    userType,
    authorizations,
    selectedAssignmentMode,
    newAuthorizations,
    funds: allFunds,
    creation,
    wizardMode
  } = props;
  // when perf debugging, check what's changed in the properties
  //useWhyDidYouUpdate('UserAuthorizationFundAdmin', props);
  const classes = useStyles();

  const uniqAuthorizations = useMemo(() => {
    return uniq(authorizations.map(item => item.fundId));
  }, [authorizations]);
  // Query to get all funds then to find theirs fund families
  return (
    <Query
      type="GET_MANY"
      resource="funds"
      payload={{
        ids: uniqAuthorizations
      }}
    >
      {({ data: funds, total, loading }) => {
        if (loading) {
          return <Progress />;
        } else if (total > 0) {
          // Retrieving fund families from funds
          const fundFamiliesIds = [];
          funds.forEach(item => {
            if (item.fundFamilyId) {
              fundFamiliesIds.push(item.fundFamilyId);
            }
          });

          if (fundFamiliesIds.length > 0) {
            // Query to get all fund families with the funds concerned
            return (
              <Query
                type="GET_LIST"
                resource="fundfamilies"
                payload={{
                  pagination: null
                }}
              >
                {({ data: fundFamilies, total, loading, error }) => {
                  if (loading) {
                    return <Progress />;
                  } else if (error) {
                    return (
                      <span className={classes.label}>
                        There are no Access Rights to be displayed
                      </span>
                    );
                  } else {
                    // Creation of the data to be send to the datagrid
                    // Adding the fund name, fund family id and fund family name
                    const dataWithFundFamilies = authorizations.map(auth => {
                      const fund = funds.find(item => item.id === auth.fundId);
                      const fundFamily =
                        fund &&
                        fundFamilies.find(
                          item => item.id === fund.fundFamilyId
                        );
                      return {
                        ...auth,
                        fundName: fund && fund.name,
                        fundFamilyId: fundFamily && fundFamily.id,
                        fundFamilyName: fundFamily && fundFamily.name
                      };
                    });

                    return (
                      <UserAuthorizationDatagrid
                        {...omit(props, ['newAuthorizations'])}
                        userType={userType}
                        groupBy="fundFamilyId"
                        groupByLabel="fundFamilyName"
                        optionText="fundName"
                        optionValue="fundId"
                        authorizations={
                          selectedAssignmentMode === 'import' || !creation
                            ? dataWithFundFamilies.concat(newAuthorizations)
                            : newAuthorizations
                        }
                        creation={creation}
                        wizardMode={wizardMode}
                      />
                    );
                  }
                }}
              </Query>
            );
          } else {
            // return the Datagrid when no fund families found with only the funds
            const dataWithFunds = authorizations.map(auth => {
              const fund = allFunds.find(item => item.id === auth.fundId);
              return {
                ...auth,
                fundName: fund && fund.name
              };
            });
            return (
              <UserAuthorizationDatagrid
                {...omit(props, ['newAuthorizations'])}
                userType={userType}
                groupBy="fundFamilyId"
                groupByLabel="fundFamilyName"
                optionText="fundName"
                optionValue="fundId"
                authorizations={dataWithFunds.concat(newAuthorizations)}
                wizardMode={wizardMode}
              />
            );
          }
        }

        return (
          <span className={classes.label}>
            There are no Access Rights to be displayed
          </span>
        );
      }}
    </Query>
  );
};

export default UserAuthorizationUpdate;
